import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import Head from 'components/Head';

import { Button, Layout, Link, Text } from 'ds';

import { ACCOUNT_PROFILE_PATH, AUTH_PATH, SLACK_USER_OAUTH_CALLBACK_PATH } from 'routes';

import { getBaseUrl, isDevelop } from 'helpers/environment';
import { completeSlackUserOAuth } from 'ux/Customer/Account/Profile/slackRequests';

const SlackUserOAuthCallback: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isSessionError, setIsSessionError] = useState(false);
  const [redirectIsComplete, setRedirectIsComplete] = useState<boolean>(isDevelop() ? false : true);

  useEffect(() => {
    if (!isDevelop()) return;

    if (window.location.host.includes('ngrok')) {
      const url = new URL(SLACK_USER_OAUTH_CALLBACK_PATH, getBaseUrl());
      url.search = window.location.search;
      window.location.href = url.href;
    }

    setRedirectIsComplete(true);
  }, []);

  useEffect(() => {
    if (!redirectIsComplete) return;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (code === null || state === null) {
      setError('Missing code or state from Slack');
      return;
    }

    completeSlackUserOAuth(code, state)
      .then(response => {
        if (response?.data?.success) {
          history.replace(ACCOUNT_PROFILE_PATH);
        } else {
          setError(response?.data?.error ?? 'Slack account connection failed');
        }
      })
      .catch(err => {
        if (err?.response?.status === 401) {
          setIsSessionError(true);
          setError('Your session has expired. Please sign in and connect Slack from your profile settings.');
          return;
        }
        const message = err?.response?.data?.error ?? 'An error occurred connecting your Slack account';
        setError(message);
      });
  }, [history, location.search, redirectIsComplete]);

  return (
    <Layout
      direction="column"
      justify="center"
      align="center"
      paddingY={80}
      paddingX={24}
      width="100%"
      minHeight="60vh"
    >
      <Head title="Connecting Slack Account" />
      {error ? (
        <Layout direction="column" align="center" gap={16}>
          <Text size="body-lg" color="red-700">
            {error}
          </Text>
          {isSessionError ? (
            <Link href={`${AUTH_PATH}?return_to=${encodeURIComponent(ACCOUNT_PROFILE_PATH)}`}>
              <Button text="Sign in" type="primary" size="xs" />
            </Link>
          ) : (
            <Link href={ACCOUNT_PROFILE_PATH}>
              <Button text="Go to profile settings" type="secondaryOutline" size="xs" />
            </Link>
          )}
        </Layout>
      ) : (
        <Text size="body-lg" color="gray-700">
          Connecting your Slack account ...
        </Text>
      )}
    </Layout>
  );
};

export default SlackUserOAuthCallback;
